import { Controller } from "@hotwired/stimulus"

// Promo code field on the free-trial checkout (fill_payment_method).
//
// Two kinds of code come back from /subscriptions/validate_promo_code:
// - trial extension (100%-off coupon): the trial length, charge dates and
//   button text all update to the longer trial
// - percent/amount discount (#3027): the trial stays its default length and
//   the pricing summary re-renders with the discounted amounts — the
//   discount itself is attached when the trial ends
export default class extends Controller {
  static targets = [
    "input",
    "feedback",
    "hiddenField",
    "buttonText",
    "trialText",
    "chargeDate",
    "headerTrialMonths",
    "headerChargeDate",
    "summaryBox",
    "summaryPrice",
    "summaryNote"
  ]

  validate() {
    const code = this.inputTarget.value.trim().toUpperCase()

    if (!code) {
      this.clearFeedback()
      return
    }

    this.feedbackTarget.innerHTML = `
      <div class="text-muted font-14">
        <span class="spinner-border spinner-border-sm me-2"></span>
        Validating...
      </div>
    `
    this.feedbackTarget.classList.remove('d-none')

    // The selected plan lets the server enforce Stripe's minimum_amount
    // restriction (annual-only codes).
    const planId = document.getElementById('selected-plan-id')?.value || ''

    fetch(`/subscriptions/validate_promo_code?code=${encodeURIComponent(code)}&plan_id=${encodeURIComponent(planId)}`, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('[name=csrf-token]').content
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.valid) {
        this.showSuccess(data)
      } else {
        this.showError(data.error)
      }
    })
    .catch(error => {
      console.error('Error validating promo code:', error)
      this.showError('Unable to validate promo code. Please try again.')
    })
  }

  showSuccess(data) {
    this.feedbackTarget.innerHTML = `
      <div class="alert alert-success py-2 mb-0 font-14" role="alert">
        <i class="fas fa-check-circle me-2"></i>
        ${data.message}
      </div>
    `
    this.feedbackTarget.classList.remove('d-none')
    this.hiddenFieldTarget.value = data.code

    if (data.discount) {
      // Discount promo: trial length, charge date and button stay default —
      // only the pricing summary changes.
      this.restoreTrialTexts()
      this.applyDiscountToSummary(data)
      return
    }

    this.restoreSummary()
    const months = data.trial_months || 1

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = `Start ${months}-Month Free Trial`
    }

    if (this.hasTrialTextTarget) {
      this.trialTextTarget.textContent = `Unlimited free access for ${months} months`
    }

    if (this.hasChargeDateTarget && data.charge_date) {
      this.chargeDateTarget.textContent = data.charge_date
    }

    // Header: "Start 6-Month Free Trial today"
    if (this.hasHeaderTrialMonthsTarget) {
      this.headerTrialMonthsTarget.textContent = months
    }

    // Header: "First billed on May 14, 2025"
    if (this.hasHeaderChargeDateTarget && data.charge_date) {
      const year = new Date().getFullYear()
      const fullDate = new Date(`${data.charge_date}, ${year}`)
      this.headerChargeDateTarget.textContent = fullDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
  }

  showError(message) {
    this.feedbackTarget.innerHTML = `
      <div class="alert alert-danger py-2 mb-0 font-14" role="alert">
        <i class="fas fa-exclamation-circle me-2"></i>
        ${message}
      </div>
    `
    this.feedbackTarget.classList.remove('d-none')
    this.clearPromoCode()
  }

  clearFeedback() {
    this.feedbackTarget.innerHTML = ''
    this.feedbackTarget.classList.add('d-none')
    this.clearPromoCode()
  }

  clearPromoCode() {
    this.hiddenFieldTarget.value = ''
    this.restoreTrialTexts()
    this.restoreSummary()
  }

  // ------------------------------------------------ pricing summary (discount)

  applyDiscountToSummary(data) {
    if (!this.hasSummaryBoxTarget || !this.hasSummaryPriceTarget) return

    const box = this.summaryBoxTarget
    const yearly = box.dataset.yearlyBilling === 'true'
    const regMonthly = parseFloat(box.dataset.regMonthly || '0')
    const regYearly = parseFloat(box.dataset.regYearly || '0')

    let discountedMonthly = regMonthly
    let discountedYearly = regYearly
    if (data.percent_off) {
      const factor = 1 - data.percent_off / 100
      discountedMonthly = regMonthly * factor
      discountedYearly = regYearly * factor
    } else if (data.amount_off_cents) {
      // amount_off applies per invoice: to the yearly invoice on annual
      // billing, to each monthly invoice otherwise.
      const off = data.amount_off_cents / 100
      if (yearly) {
        discountedYearly = Math.max(regYearly - off, 0)
        discountedMonthly = discountedYearly / 12
      } else {
        discountedMonthly = Math.max(regMonthly - off, 0)
      }
    } else {
      return
    }

    if (this.originalSummaryPrice === undefined) {
      this.originalSummaryPrice = this.summaryPriceTarget.innerHTML
    }
    if (this.hasSummaryNoteTarget && this.originalSummaryNote === undefined) {
      this.originalSummaryNote = this.summaryNoteTarget.innerHTML
    }

    this.summaryPriceTarget.innerHTML =
      `<strong>${this.formatMoney(discountedMonthly)}/mo</strong>` +
      (yearly ? `, billed ${this.formatMoney(discountedYearly)}/yr` : '')

    if (this.hasSummaryNoteTarget) {
      this.summaryNoteTarget.textContent =
        `First month free. ${this.discountLabel(data)} after your trial (reg. ${this.formatMoney(regMonthly)}/mo). Cancel anytime.`
    }
  }

  restoreSummary() {
    if (this.originalSummaryPrice !== undefined && this.hasSummaryPriceTarget) {
      this.summaryPriceTarget.innerHTML = this.originalSummaryPrice
    }
    if (this.originalSummaryNote !== undefined && this.hasSummaryNoteTarget) {
      this.summaryNoteTarget.innerHTML = this.originalSummaryNote
    }
  }

  discountLabel(data) {
    const amount = data.percent_off
      ? `${data.percent_off % 1 === 0 ? Math.round(data.percent_off) : data.percent_off}%`
      : this.formatMoney(data.amount_off_cents / 100)

    if (data.duration === 'repeating') {
      const months = data.duration_in_months
      return `${amount} off for ${months} ${months === 1 ? 'month' : 'months'}`
    }
    if (data.duration === 'forever') return `${amount} off for as long as you subscribe`
    return `${amount} off your first payment`
  }

  formatMoney(value) {
    const rounded = Math.round(value * 100) / 100
    return '$' + rounded.toLocaleString('en-US', {
      minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
      maximumFractionDigits: 2
    })
  }

  // -------------------------------------------------- trial copy (defaults)

  restoreTrialTexts() {
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = 'Start 1-Month Free Trial'
    }

    if (this.hasTrialTextTarget) {
      this.trialTextTarget.textContent = 'Unlimited free access for 1 month'
    }

    const defaultTrialDays = parseInt(document.body.dataset.trialDays || 30)
    const defaultDate = new Date()
    defaultDate.setDate(defaultDate.getDate() + defaultTrialDays)

    if (this.hasChargeDateTarget) {
      this.chargeDateTarget.textContent = defaultDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }

    if (this.hasHeaderTrialMonthsTarget) {
      this.headerTrialMonthsTarget.textContent = '1'
    }

    if (this.hasHeaderChargeDateTarget) {
      this.headerChargeDateTarget.textContent = defaultDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
  }
}
